import { FC, useEffect, useState } from 'react'
import { Button, InfoAlert } from '../../../components'
import { ButtonType } from '../../../../_metronic/helpers'

type RecoveryCodesProps = {
  codigos: string[]
  onConfirm?: () => void
  confirmLabel?: string
  isSubmitting?: boolean
}

const TEMPO_AVISO_COPIA = 3000

/**
 * Exibição única dos códigos de recuperação. Depois desta tela eles só existem como hash no
 * servidor, então a confirmação explícita evita que a pessoa siga adiante sem guardá-los.
 */
export const RecoveryCodes: FC<RecoveryCodesProps> = ({
  codigos,
  onConfirm,
  confirmLabel = 'Continuar',
  isSubmitting = false,
}) => {
  const [guardou, setGuardou] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [erroCopia, setErroCopia] = useState(false)

  useEffect(() => {
    if (!copiado) return
    const timer = setTimeout(() => setCopiado(false), TEMPO_AVISO_COPIA)
    return () => clearTimeout(timer)
  }, [copiado])

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigos.join('\n'))
      setErroCopia(false)
      setCopiado(true)
    } catch {
      setCopiado(false)
      setErroCopia(true)
    }
  }

  /**
   * O Firefox lê o blob de forma assíncrona e só baixa links presentes no documento: anexar o
   * elemento e adiar a revogação da URL evita que o download seja cancelado.
   */
  const baixar = () => {
    const blob = new Blob([codigos.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'lauduz-codigos-recuperacao.txt'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  return (
    <div>
      <InfoAlert variant='warning' title='Guarde estes códigos agora'>
        Eles não serão exibidos de novo. Cada código funciona uma única vez e serve para entrar caso
        você perca o acesso ao aplicativo autenticador.
      </InfoAlert>

      <ul className='list-unstyled row row-cols-2 g-2 mb-5' aria-label='Códigos de recuperação'>
        {codigos.map((codigo) => (
          <li key={codigo} className='col'>
            <code className='d-block bg-light rounded py-2 px-3 fs-5 text-center text-dark'>
              {codigo}
            </code>
          </li>
        ))}
      </ul>

      <div className='d-flex flex-wrap gap-3 mb-5'>
        <Button onClick={copiar} className='btn-light-primary flex-grow-1'>
          {copiado ? 'Copiado' : 'Copiar códigos'}
        </Button>
        <Button onClick={baixar} className='btn-light-primary flex-grow-1'>
          Baixar em arquivo
        </Button>
      </div>

      {erroCopia && (
        <InfoAlert variant='danger' showIcon={false}>
          Não foi possível copiar os códigos. Baixe o arquivo ou anote-os manualmente.
        </InfoAlert>
      )}

      {onConfirm && (
        <>
          <div className='form-check mb-5'>
            <input
              className='form-check-input'
              type='checkbox'
              id='confirma-codigos-guardados'
              checked={guardou}
              onChange={(event) => setGuardou(event.target.checked)}
            />
            <label className='form-check-label' htmlFor='confirma-codigos-guardados'>
              Guardei meus códigos de recuperação em um lugar seguro
            </label>
          </div>

          <Button
            type={ButtonType.Button}
            onClick={onConfirm}
            className='btn-primary btn-auth-full'
            disabled={!guardou || isSubmitting}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    </div>
  )
}
